import { useState, useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import LinkIcon from "@atlaskit/icon/glyph/link";
import LinkFilledIcon from "@atlaskit/icon/glyph/link-filled";
import MediaServicesLineIcon from "@atlaskit/icon/glyph/media-services/line";
import UnlinkIcon from "@atlaskit/icon/glyph/unlink";
import { token } from "@atlaskit/tokens";

import { messages } from "../messages";
import { Actions, Epic, unlinkDependency } from "../state";
import { useAppDispatch } from "../state/hook";

import { StoryRowItem } from "./story-row-item";

interface DependencyLinkProps {
    interactiveState: string;
    onPress: () => void;
    isDemoComplete: boolean;
    epics: Epic[];
}

export const DependencyLink = ({
    interactiveState,
    onPress,
    isDemoComplete,
    epics
}: DependencyLinkProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const dispatch = useAppDispatch();
    const [firstStory, setFirstStory] = useState(epics[0]?.children[1]?.name);
    const [secondStory, setSecondStory] = useState(epics[0]?.children[2]?.name);
    const [isDependencyExpanded, setIsDependencyExpanded] = useState(true);
    // if user resets demo
    if (!isDemoComplete && !isDependencyExpanded) {
        setIsDependencyExpanded(true);
    }

    useEffect(() => {
        setFirstStory(epics[0]?.children[0]?.name);
        setSecondStory(epics[0]?.children[2]?.name);
    }, [epics]);

    // if user unlinks
    if (!isDependencyExpanded) {
        return null;
    }
    const dependencyUnLinkClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapUnlinkDependency",
            eventType: "ui"
        }).fire();
        setIsDependencyExpanded(false);
        dispatch(unlinkDependency());
    };
    if (interactiveState === Actions.MAP_PROJECT_DEPENDENCIES) {
        return (
            <div
                css={DependencyLinkContainer}
                data-testid="dependency-link-clickable"
            >
                <FormattedMessage {...messages.linkStoriesButton}>
                    {(label) => (
                        <button
                            css={StyleLinkIconHover}
                            className={"style-link-icon-hover"}
                            aria-label={label as unknown as string}
                            onClick={onPress}
                            data-testid={"link-icon-clickable"}
                        >
                            <LinkIcon
                                size="small"
                                label=""
                                primaryColor="#FFF"
                            />
                        </button>
                    )}
                </FormattedMessage>
                <FormattedMessage {...messages.linkStoriesButton}>
                    {(label) => (
                        <button
                            css={StyleSvgHover}
                            aria-label={label as unknown as string}
                            onClick={onPress}
                        >
                            <svg
                                id="dependency-svg"
                                width="25"
                                height="70"
                                viewBox="0 0 25 70"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M 10 1 V 1 C 24 8 21 22 12 34 V 34 C -1 49 1 62 10 69"
                                    stroke="#FF8B00"
                                    strokeWidth="2"
                                />
                            </svg>
                        </button>
                    )}
                </FormattedMessage>
            </div>
        );
    } else if (isDemoComplete || interactiveState === Actions.COMPLETE_DEMO) {
        return (
            <div
                css={DependencyLinkContainer}
                data-testid="dependency-link-not-clickable"
            >
                <div css={StyleLinkIcon}>
                    <LinkIcon size="small" label="" primaryColor="#FFF" />
                </div>
                <div css={StyleSvg}>
                    <svg
                        width="25"
                        height="70"
                        viewBox="0 0 25 70"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M 10 1 V 1 C 24 8 21 22 12 34 V 34 C -1 49 1 62 10 69"
                            stroke="#FF8B00"
                            strokeWidth="2"
                        />
                    </svg>
                </div>
                {isDependencyExpanded && (
                    <div
                        css={VisualDepenedencyBoxContainer}
                        data-testid="dependency-link-expanded"
                    >
                        <div css={StoryContainer}>
                            <StoryRowItem
                                id="dependency-story-1"
                                epicIndex={0}
                                name={
                                    firstStory as {
                                        id: string;
                                        defaultMessage: string;
                                    }
                                }
                                storyIndex={0}
                                isEpicCol={false}
                            />
                        </div>
                        <div css={LinkIconRow}>
                            <DependencyLinkLine />
                            <FormattedMessage
                                {...messages.unlinkChildStoryButton}
                            >
                                {(label) => (
                                    <button
                                        css={UnlinkWrapper}
                                        aria-label={label as unknown as string}
                                        onClick={dependencyUnLinkClick}
                                        data-testid="dependency-unlink-btn"
                                        id="dependency-unlink-btn"
                                    >
                                        <UnlinkIcon
                                            label=""
                                            primaryColor={"#42526E"}
                                        />
                                    </button>
                                )}
                            </FormattedMessage>
                        </div>
                        <div css={StoryContainer}>
                            <StoryRowItem
                                id="dependency-story-2"
                                epicIndex={0}
                                name={
                                    secondStory as {
                                        id: string;
                                        defaultMessage: string;
                                    }
                                }
                                storyIndex={2}
                                isEpicCol={false}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const StyleSvgHover = css({
    border: "none",
    padding: 0,
    backgroundColor: "transparent",
    marginBlockStart: "14px",
    height: "fit-content",
    width: "fit-content",
    zIndex: 0,
    strokeDasharray: 500,
    strokeDashoffset: 500,
    animation: "dash 2.5s ease-in-out forwards",
    path: {
        transition: "0.5s"
    },
    ["&:focus, &:hover"]: {
        path: {
            stroke: "#aa3013"
        }
    },
    ["@keyframes dash"]: {
        to: {
            strokeDashoffset: 0
        }
    }
});
const StyleSvg = css({
    marginBlockStart: "14px",
    height: "fit-content",
    width: "fit-content",
    zIndex: 0
});

const StyleLinkIconHover = css({
    backgroundColor: "transparent",
    border: "none",
    marginInlineEnd: "-5px",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    zIndex: 2,
    transition: "0.25s",
    ["&:focus, &:hover"]: {
        backgroundColor: "rgba(0, 0, 0, 0.2)"
    }
});
const StyleLinkIcon = css({
    marginBlockStart: "3px",
    display: "flex",
    marginInlineEnd: "-7px",
    alignItems: "center",
    justifyContent: "center",
    height: "18px",
    width: "18px",
    borderRadius: "100%",
    zIndex: 2,
    transition: "0.25s"
});

const DependencyLinkContainer = css({
    position: "absolute",
    insetBlockStart: "40px",
    insetInlineStart: "475px",
    display: "flex",
    flexDirection: "row",
    width: "fit-content",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    zIndex: 2,
    [".style-link-icon-hover"]: {
        marginBlockStart: "4px"
    }
});
const VisualDepenedencyBoxContainer = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    backgroundColor: "#EBECF0",
    borderRadius: "3px",
    boxShadow: "1px 3px 3px rgba(0, 0, 0, 0.2)",
    padding: "16px",
    marginBlockStart: "25px",
    marginInlineStart: "5px",
    zIndex: 2
});
const StoryContainer = css({
    backgroundColor: "#fff",
    borderRadius: "4px",
    width: "200px",
    padding: "2px 10px",
    zIndex: 1
});
const Line = css({
    transform: "rotate(-45deg)",
    margin: "-5px 0px"
});
const LinkLineWrapper = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginInlineStart: "30px"
});
const LinkIconRow = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "200px"
});
const baseShadow = `0 0 0 1px ${token(
    "color.border.focused",
    "rgba(29, 122, 252, 1)"
)}`;
const UnlinkWrapper = css({
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(29, 122, 252, 0.4)",
    border: "2px solid #0C66E4",
    borderRadius: "50%",
    margin: "10px",
    boxShadow: "0 0 0 0 rgba(29, 122, 252, 1)",
    ["@keyframes roadmapPulseLink"]: {
        from: {
            boxShadow: `${baseShadow}, 0 0 0 ${token(
                "color.border.focused",
                "rgba(56, 139, 255, 1)"
            )}`
        },
        to: {
            boxShadow: `${baseShadow}, 0 0 0 10px rgba(56, 139, 255, 0.01)`
        }
    },
    animation: "roadmapPulseLink 2s infinite"
});
const DependencyLinkLine = () => {
    return (
        <div css={LinkLineWrapper}>
            <div css={Line}>
                <MediaServicesLineIcon label="" primaryColor={"#42526E"} />
            </div>
            <LinkFilledIcon label="" primaryColor={"#42526E"} />
            <div css={Line}>
                <MediaServicesLineIcon label="" primaryColor={"#42526E"} />
            </div>
        </div>
    );
};
